/* ==========================================================================
 * BUNDLE CONFIGURATOR — Phase 1
 * --------------------------------------------------------------------------
 * Self-contained. Does not modify or depend on common.js / cart-ajax.js
 * internals; it only *reuses* their globals when they happen to exist
 * (cartUrl, buildCart) so the existing cart drawer refreshes as normal.
 *
 * Responsibilities
 *   1. Build a Colour x Size availability matrix per component group from
 *      live Liquid-rendered variant data.
 *   2. Enforce PAIR-level availability: a colour or size is only disabled
 *      outright when EVERY combination using it is unavailable.
 *   3. Compute bundle price dynamically = duvet variant price + sheet variant
 *      price. Discount hooks exist but are inert in Phase 1.
 *   4. Re-validate both selections against live product JSON immediately
 *      before adding to cart (race-condition guard).
 *   5. Add both REAL component variants to the cart, joined by a bundle id.
 *
 * Shopify remains the final authority on inventory: every component variant
 * is inventory_policy = "deny", so /cart/add.js rejects an oversell outright.
 * ========================================================================== */
(function () {
  'use strict';

  var MONEY_FALLBACK = '${{amount}}';

  /* The two component groups, in display order. Component 1 is also the source
     of the shared colour order — see applySharedColourOrder. */
  var GROUP_KEYS = ['duvet', 'sheet'];

  /* ---------------------------------------------------------------- utils */

  function formatMoney(cents, format) {
    var fmt = format || (typeof moneyFormat !== 'undefined' ? moneyFormat : MONEY_FALLBACK);
    var value = (cents || 0) / 100;

    function withDelimiters(num, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(num) || num === null) return '0';
      var fixed = num.toFixed(precision);
      var parts = fixed.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cents_ = parts[1] ? decimal + parts[1] : '';
      return dollars + cents_;
    }

    var match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) return fmt;

    var formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':
        formatted = withDelimiters(value, 0, ' ', ','); break;
      default:
        formatted = withDelimiters(value, 2);
    }
    return fmt.replace(match[0], formatted);
  }

  function unique(list) {
    var seen = {}, out = [];
    list.forEach(function (v) {
      if (v !== undefined && v !== null && !seen[v]) { seen[v] = 1; out.push(v); }
    });
    return out;
  }

  /* A colour with no swatch image in the custom.variant_image_N metafield map
     is a data gap, not something to paper over with a guessed colour. Report
     it once per colour so it can be fixed in Shopify Admin. */
  /* BUNDLE pricing, and the ONLY pricing source: the per-variant
     bundle.component_selling_price / bundle.component_compare_at_price
     metafields, resolved in Liquid. Nothing here reads variant.price,
     compare_at_price, the parent product price or a market price. */
  function priceOf(item) {
    return item ? (Number(item.bundlePrice) || 0) : 0;
  }

  function compareAtOf(item) {
    return item ? (Number(item.bundleCompareAt) || 0) : 0;
  }

  /* False when no Bundle Selling Price is configured. Such a variant has no
     price at all — it is not priced from anything else. */
  function isPriced(item) {
    return !!(item && item.priceConfigured);
  }

  /* What Shopify will actually charge for this variant at /cart/add.js. Used
     ONLY to detect a PDP-vs-cart divergence — never to price the bundle. */
  function nativePriceOf(item) {
    return item ? (Number(item.nativePrice) || 0) : 0;
  }

  /* Renders one component's price cell from its configured BUNDLE values. */
  function renderComponentPrice(el, item, money) {
    if (!el) return;
    if (!item || !item.available) { el.textContent = '—'; return; }

    var price = priceOf(item);
    var cmp = compareAtOf(item);

    el.textContent = '';
    var now = document.createElement('span');
    now.className = 'bundlecfg__price-now';
    now.textContent = formatMoney(price, money);
    el.appendChild(now);

    if (cmp > price) {
      var was = document.createElement('s');
      was.className = 'bundlecfg__price-was';
      was.textContent = formatMoney(cmp, money);
      el.appendChild(document.createTextNode(' '));
      el.appendChild(was);
    }
  }

  var reportedSwatches = {};
  function reportMissingSwatch(group, colour) {
    var key = group + '::' + colour;
    if (reportedSwatches[key]) return;
    reportedSwatches[key] = true;
    console.warn(
      '[bundle-configurator] No swatch image for "' + colour + '" in the ' +
      group + ' component. Add it to the custom.variant_image_N / custom.color_N ' +
      'metafield mapping on the component product. No colour has been guessed.'
    );
  }

  /* Arrow-key navigation within a radiogroup, per WAI-ARIA. */
  function bindRovingKeys(listEl) {
    listEl.addEventListener('keydown', function (e) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(e.key) === -1) return;
      var btns = Array.prototype.slice
        .call(listEl.querySelectorAll('button'))
        .filter(function (b) { return !b.disabled; });
      if (!btns.length) return;
      var i = btns.indexOf(document.activeElement);
      var next;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = btns.length - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % btns.length;
      else next = (i - 1 + btns.length) % btns.length;
      e.preventDefault();
      btns[next].focus();
      btns[next].click();
    });
  }

  /* ------------------------------------------------------------ the group */

  function Group(key, data, root) {
    this.key = key;
    this.label = data.label;
    this.items = data.items || [];
    this.root = root;
    this.el = root.querySelector('[data-bundle-group="' + key + '"]');
    this.colour = null;
    this.size = null;

    this.colours = unique(this.items.map(function (i) { return i.colour; }));
    this.sizes = unique(this.items.map(function (i) { return i.size; }));
    /* colour -> [imageUrl], from the standalone component product's media */
    this.images = data.images || {};
  }

  /* --------------------------------------------------------------------------
     SHARED COLOUR ORDER
     --------------------------------------------------------------------------
     Each group's swatch order falls out of the order its component products
     were picked in the theme editor, and the two lists were curated
     separately — so the same colour could sit first in one row and third in the
     other, which made the rows impossible to read against each other.

     Component 1's order is canonical. Both rows follow it, and any colour only
     Component 2 carries is appended in its own order, so nothing is ever
     dropped. Each group still renders exactly the colours it stocks — this only
     changes the sequence. To change the order, reorder Component 1's product
     list in the theme editor and both rows follow.
     -------------------------------------------------------------------------- */
  function applySharedColourOrder(groups) {
    var order = [];
    GROUP_KEYS.forEach(function (k) {
      if (!groups[k]) return;
      groups[k].colours.forEach(function (c) {
        if (order.indexOf(c) === -1) order.push(c);
      });
    });

    GROUP_KEYS.forEach(function (k) {
      if (!groups[k]) return;
      var own = groups[k].colours;
      groups[k].colours = order.filter(function (c) {
        return own.indexOf(c) !== -1;
      });
    });
  }

  /* Images for this group's currently selected colour.

     Responds to COLOUR only. No component variant carries its own image
     (0 of 36), so switching size cannot change the pictures — there are none
     to switch to. Size-specific imagery would mean assigning images to
     variants in Admin first. */
  Group.prototype.currentImages = function () {
    return (this.colour && this.images[this.colour]) || [];
  };

  /* Short display label for a size ("Queen 3PC (90" X 94")" -> "Queen").
     Presentation only — `size` remains the full variant title everywhere that
     matters: matching, availability lookups and the cart line property. */
  Group.prototype.sizeLabelOf = function (size) {
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].size === size) {
        return this.items[i].sizeLabel || size;
      }
    }
    return size;
  };

  Group.prototype.find = function (colour, size) {
    for (var i = 0; i < this.items.length; i++) {
      if (this.items[i].colour === colour && this.items[i].size === size) {
        return this.items[i];
      }
    }
    return null;
  };

  /* PAIR-level availability. A colour stays selectable while ANY size in that
     colour is available; a size stays selectable while ANY colour in that size
     is available. Only a genuinely dead axis is disabled outright. */
  Group.prototype.colourHasStock = function (colour) {
    return this.items.some(function (i) { return i.colour === colour && i.available; });
  };

  Group.prototype.sizeHasStock = function (size) {
    return this.items.some(function (i) { return i.size === size && i.available; });
  };

  Group.prototype.pairAvailable = function (colour, size) {
    var item = this.find(colour, size);
    return !!(item && item.available);
  };

  Group.prototype.selected = function () {
    if (!this.colour || !this.size) return null;
    return this.find(this.colour, this.size);
  };

  Group.prototype.isComplete = function () {
    var item = this.selected();
    return !!(item && item.available);
  };

  /* Pick a sensible opening state: first colour with stock, then first size
     that is actually available in that colour. Never auto-selects a dead pair. */
  Group.prototype.preselect = function () {
    var self = this;
    var colour = this.colours.filter(function (c) { return self.colourHasStock(c); })[0];
    if (!colour) colour = this.colours[0];
    this.colour = colour || null;

    var size = this.sizes.filter(function (s) { return self.pairAvailable(self.colour, s); })[0];
    if (!size) size = this.sizes.filter(function (s) { return self.sizeHasStock(s); })[0];
    if (!size) size = this.sizes[0];
    this.size = size || null;
  };

  Group.prototype.render = function () {
    var self = this;
    var colourList = this.el.querySelector('[data-bundle-colours]');
    var sizeList = this.el.querySelector('[data-bundle-sizes]');

    /* ----- colours ----- */
    colourList.innerHTML = '';
    this.colours.forEach(function (colour) {
      var dead = !self.colourHasStock(colour);
      var blockedWithSize = self.size && !self.pairAvailable(colour, self.size);
      var sample = self.items.filter(function (i) { return i.colour === colour; })[0] || {};

      var li = document.createElement('li');
      li.className = 'bundlecfg__swatch-item';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundlecfg__swatch';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(self.colour === colour));
      btn.setAttribute('title', colour);
      btn.setAttribute('aria-label', colour);
      btn.dataset.colour = colour;
      /* roving tabindex: only the selected swatch is in the tab order */
      btn.tabIndex = self.colour === colour ? 0 : -1;

      /* The swatch image comes exclusively from the store's existing
         custom.variant_image_N metafield mapping. There is deliberately NO
         fallback to the product photo and NO CSS-colour-name guess: an
         unresolved swatch is reported, never invented. */
      if (sample.swatchImage) {
        var img = document.createElement('img');
        img.className = 'bundlecfg__swatch-img';
        img.src = sample.swatchImage;
        img.alt = '';
        img.setAttribute('aria-hidden', 'true');
        img.loading = 'lazy';
        btn.appendChild(img);
      } else {
        btn.classList.add('is-missing-swatch');
        reportMissingSwatch(self.key, colour);
      }

      if (self.colour === colour) btn.classList.add('is-selected');

      /* A colour is non-selectable when it is unavailable in the CURRENTLY
         selected size, as well as when it is sold out in every size. Both get
         the same blocked treatment so a mis-click cannot land on a combination
         that does not exist — the customer changes size first, then sees which
         colours that size actually offers.

         This is still pair-level: the colour is not disabled outright, it is
         disabled *given this size*. Switching size restores it. */
      if (dead || blockedWithSize) {
        btn.classList.add('is-unavailable');
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.tabIndex = -1;
        var why = dead
          ? colour + ' — sold out in every size'
          : colour + ' — not available in ' + self.sizeLabelOf(self.size) + '. Choose another size to see it.';
        btn.title = why;
        btn.setAttribute('aria-label', why);
      }

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.colour = colour;
        /* Interacting with a component makes its imagery the active gallery. */
        self.root.__bundle.setActiveGallery(self.key);
        self.root.__bundle.update();
      });

      li.appendChild(btn);
      colourList.appendChild(li);
    });

    /* ----- sizes ----- */
    sizeList.innerHTML = '';
    this.sizes.forEach(function (size) {
      var dead = !self.sizeHasStock(size);
      var blockedWithColour = self.colour && !self.pairAvailable(self.colour, size);

      var li = document.createElement('li');
      li.className = 'bundlecfg__size-item';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundlecfg__size';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', String(self.size === size));
      btn.dataset.size = size;
      btn.textContent = self.sizeLabelOf(size);
      btn.setAttribute('aria-label', size);   /* full title for screen readers */
      btn.tabIndex = self.size === size ? 0 : -1;

      if (self.size === size) btn.classList.add('is-selected');
      if (dead || blockedWithColour) {
        btn.classList.add('is-unavailable');
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        btn.title = dead
          ? size + ' — sold out'
          : size + ' — not available in ' + self.colour;
      }

      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        self.size = size;
        self.root.__bundle.setActiveGallery(self.key);
        self.root.__bundle.update();
      });

      li.appendChild(btn);
      sizeList.appendChild(li);
    });

    /* ----- readouts ----- */
    var selColour = this.el.querySelector('[data-bundle-selected-colour]');
    var selSize = this.el.querySelector('[data-bundle-selected-size]');
    var summary = this.el.querySelector('[data-bundle-summary]');
    var error = this.el.querySelector('[data-bundle-group-error]');

    if (selColour) selColour.textContent = this.colour || '';
    if (selSize) selSize.textContent = this.size ? this.sizeLabelOf(this.size) : '';

    var item = this.selected();
    if (summary) {
      summary.textContent = item && item.available
        ? this.size + ' / ' + this.colour
        : '';
    }

    if (error) {
      if (this.colour && this.size && (!item || !item.available)) {
        error.hidden = false;
        error.textContent = this.colour + ' / ' + this.size + ' is currently unavailable. Choose another combination.';
      } else {
        error.hidden = true;
        error.textContent = '';
      }
    }

  };

  /* ----------------------------------------------------------- controller */

  function Configurator(root) {
    this.root = root;
    root.__bundle = this;

    /* The data island is emitted by the data-only section, which sits
       elsewhere in the document, so fall back to a document-wide lookup. */
    var node = root.querySelector('[data-bundle-component-data]') ||
               document.querySelector('[data-bundle-component-data]');

    if (!node) {
      this.fail(root,
        'No component data found.',
        'Add the "Bundle configurator data" section to this template and select ' +
        'the Duvet and Sheet colour products in its settings.');
      return;
    }

    try {
      this.data = JSON.parse(node.textContent);
    } catch (e) {
      console.error('[bundle-configurator] component data is not valid JSON', e);
      this.fail(root,
        'Component data is not valid JSON.',
        'Check the browser console for the parse error.');
      return;
    }

    this.money = this.data.moneyFormat;
    this.bundle = this.data.bundle;

    this.groups = {
      duvet: new Group('duvet', this.data.groups.duvet, root),
      sheet: new Group('sheet', this.data.groups.sheet, root)
    };

    /* Both swatch rows list their colours in the same sequence. Must run before
       preselect(), which opens on the first in-stock colour. */
    applySharedColourOrder(this.groups);

    /* Fallback pricing must never be silent. Liquid resolved it and counted it;
       this surfaces it, naming the variants so they can be fixed in Admin. */
    var unpriced = this.data.pricing && Number(this.data.pricing.unpriced);
    if (unpriced > 0) {
      var missing = [];
      var all = this.groups;
      GROUP_KEYS.forEach(function (k) {
        all[k].items.forEach(function (i) {
          if (!isPriced(i)) missing.push(k + ' ' + i.colour + ' / ' + i.sizeLabel);
        });
      });
      console.warn(
        '[bundle-configurator] ' + unpriced + ' component variant(s) have no ' +
        '"Bundle Selling Price" and therefore cannot be sold in the bundle: ' +
        missing.join(', ') + '. Set bundle.component_selling_price on each ' +
        'variant in Shopify Admin. There is no fallback to the standalone price.'
      );
    }

    /* Unconfigured bundle inventory reads as out of stock, which looks
       identical to a deliberate 0 on the storefront. Distinguish them here so
       "never set up" is not mistaken for "sold out". */
    var uninv = this.data.pricing && Number(this.data.pricing.uninventoried);
    if (uninv > 0) {
      console.warn(
        '[bundle-configurator] ' + uninv + ' component variant(s) have no ' +
        '"Bundle Inventory" set and are therefore unavailable in the bundle. ' +
        'This is NOT the same as being sold out — set ' +
        'bundle.component_inventory_quantity in Shopify Admin to allocate ' +
        'units to the bundle. Standalone stock is deliberately not inherited.'
      );
    }

    this.addBtn = root.querySelector('[data-bundle-add]');
    this.addLabel = root.querySelector('[data-bundle-add-label]');
    this.errorEl = root.querySelector('[data-bundle-error]');

    /* Labels come from the data island so the section settings stay
       authoritative even though the shell is rendered by a separate block. */
    var groups = this.groups;
    GROUP_KEYS.forEach(function (k) {
      var label = groups[k].label;
      if (!label) return;
      var head = root.querySelector('[data-bundle-group="' + k + '"] [data-bundle-group-title]');
      var name = root.querySelector('[data-bundle-' + k + '-name]');
      if (head) head.textContent = label;
      if (name) name.textContent = label;
    });

    /* Shell ships hidden so a configurator with no data never flashes empty. */
    root.hidden = false;

    /* Take over the main gallery, starting on the first component. */
    this.activeGroupKey = 'duvet';
    this.initGallery();
    this.initAccordionLayout();

    this.groups.duvet.preselect();
    this.groups.sheet.preselect();

    /* If we arrived from a cart line, replace those defaults with the exact
       configuration that was in the cart. Async, so update() runs again once
       the answer lands; the preselect above keeps the page coherent meanwhile. */
    var selfR = this;
    this.restoreFromVariantParam(function () { selfR.update(); });

    /* Bind arrow-key navigation once per list; render() replaces the buttons
       inside these containers but never the containers themselves. */
    root.querySelectorAll('[data-bundle-colours], [data-bundle-sizes]')
      .forEach(bindRovingKeys);

    var self = this;
    this.addBtn.addEventListener('click', function () { self.addToCart(); });

    /* Quantity control: +/- buttons and direct entry, both clamped to the
       smaller component stock. */
    var qtyInput = root.querySelector('[data-bundle-quantity]');
    root.querySelectorAll('[data-bundle-qty-step]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var step = parseInt(btn.dataset.bundleQtyStep, 10) || 0;
        var next = (parseInt(qtyInput.value, 10) || 1) + step;
        var max = self.maxQuantity();
        if (next < 1) next = 1;
        if (next > max) next = max;
        qtyInput.value = next;
        self.syncQuantityCap();
      });
    });
    if (qtyInput) {
      qtyInput.addEventListener('change', function () { self.syncQuantityCap(); });
    }

    this.update();
  }

  /* Configuration failures are silent for customers (the shell simply stays
     hidden) but visible inside the theme editor, so a misconfiguration is
     diagnosable instead of looking like a dead component. */
  Configurator.prototype.fail = function (root, title, detail) {
    this.data = null;
    console.warn('[bundle-configurator] ' + title + ' ' + detail);

    if (root.dataset.bundleDesignMode !== 'true') return;

    var box = document.createElement('div');
    box.className = 'bundlecfg-diagnostic';
    var strong = document.createElement('strong');
    strong.textContent = 'Bundle configurator: ' + title;
    var span = document.createElement('span');
    span.textContent = detail;
    box.appendChild(strong);
    box.appendChild(span);

    root.textContent = '';
    root.appendChild(box);
    root.hidden = false;
  };

  /* --------------------------------------------------------------------------
     BUNDLE TOTALS

       selling   = duvet bundle selling price + sheet bundle selling price
       compareAt = duvet bundle compare-at    + sheet bundle compare-at

     Straight sums of the two configured component values. There is no
     percentage, no promotion and no Discount Function in this path — the saving
     IS the gap between the two totals.
     -------------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     NATIVE BUNDLE RESOLUTION
     --------------------------------------------------------------------------
     The four selections resolve to ONE Shopify Native Bundle variant. That
     variant is what the cart receives, which is why the cart shows a single
     bundle line and why the charged price cannot drift from the displayed one:
     both are the same variant's market-resolved price.

     The key is "<duvet cell>|<sheet cell>", each cell being "<Colour> — <Size>"
     exactly as it appears as a component option value. Built from the item's
     own colour and sizeLabel rather than from any title string.
     -------------------------------------------------------------------------- */
  function cellOf(cfg, item) {
    if (!item) return null;
    var sep = (cfg.data.nativeBundle && cfg.data.nativeBundle.cellSeparator) || ' — ';
    return item.colour + sep + item.sizeLabel;
  }

  /* Resolution key, built from the two component option values. Never from a
     variant title: Shopify joins option values with " / " and each value here
     is itself "<Colour> — <Size>", so a title split would be ambiguous. */
  Configurator.prototype.nativeKey = function () {
    var d = this.groups.duvet.selected(), s = this.groups.sheet.selected();
    if (!d || !s) return null;
    return cellOf(this, d) + '|' + cellOf(this, s);
  };

  Configurator.prototype.usingNativeBundle = function () {
    var nb = this.data.nativeBundle;
    return !!(nb && nb.storefront && nb.storefront.token);
  };

  /* Cache of resolved variants, keyed as above. A combination is fetched once
     per page view; flipping back and forth costs nothing. */
  Configurator.prototype.nativeVariant = function () {
    var k = this.nativeKey();
    if (!k) return null;
    var hit = this.__nvCache && this.__nvCache[k];
    return (hit && hit.variant) || null;
  };

  /* --------------------------------------------------------------------------
     RESTORING A SELECTION FROM THE CART
     --------------------------------------------------------------------------
     Clicking a bundle line in the cart lands here with ?variant=<bundle variant
     id>. That id already encodes both components, so instead of guessing we ask
     Shopify what its option values are and preselect them — the customer
     returns to the exact configuration they had.

     Resolved through the Storefront API rather than a variant list, for the
     same reason the forward lookup is: a 324-variant product cannot be
     enumerated on the storefront.
     -------------------------------------------------------------------------- */
  Configurator.prototype.restoreFromVariantParam = function (done) {
    var nb = this.data.nativeBundle;
    if (!nb || !nb.storefront || !nb.storefront.token) { done(); return; }

    var m = /[?&]variant=(\d+)/.exec(window.location.search);
    if (!m) { done(); return; }

    var self = this;
    var sf = nb.storefront;
    fetch(sf.endpoint, {
      method: 'POST',
      headers: { 'X-Shopify-Storefront-Access-Token': sf.token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: 'query($id:ID!){ node(id:$id){ ... on ProductVariant { selectedOptions{ name value } } } }',
        variables: { id: 'gid://shopify/ProductVariant/' + m[1] }
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var opts = j.data && j.data.node && j.data.node.selectedOptions;
        if (!opts) return;
        var sep = nb.cellSeparator || ' — ';
        opts.forEach(function (o) {
          var key = (o.name === nb.optionNames.duvet) ? 'duvet'
            : (o.name === nb.optionNames.sheet) ? 'sheet' : null;
          if (!key) return;
          var parts = o.value.split(sep);
          if (parts.length < 2) return;
          var group = self.groups[key];
          var colour = parts[0], size = parts[1];
          /* Only apply if the option genuinely exists — a stale link must not
             leave the configurator in an impossible state. */
          if (group.colours.indexOf(colour) === -1) return;
          var match = group.items.filter(function (i) {
            return i.colour === colour && i.sizeLabel === size;
          })[0];
          if (!match) return;
          group.colour = colour;
          group.size = match.size;
        });
      })
      .catch(function (e) {
        console.warn('[bundle-configurator] could not restore cart selection: ' + e.message);
      })
      .then(function () { done(); });
  };

  /* Resolution state for the current selection. The distinction that matters is
     "we don't know yet" versus "we asked and there is nothing" — the first must
     read as loading, the second as unavailable. Conflating them is what made a
     price flash an error message mid-switch. */
  Configurator.prototype.nativeState = function () {
    var k = this.nativeKey();
    if (!k) return 'none';
    var hit = this.__nvCache && this.__nvCache[k];
    if (!hit) return 'pending';        /* not requested yet */
    return hit.state;                  /* 'loading' | 'done' */
  };

  /* Button-level busy state while a combination is being resolved. Deliberately
     not setError(): nothing is wrong, the answer simply has not arrived. */
  Configurator.prototype.setResolving = function (on) {
    if (this.__resolvingShown === on) return;
    this.__resolvingShown = on;
    if (this.addLabel) {
      if (on) {
        this.__addLabelText = this.__addLabelText || this.addLabel.textContent;
        this.addLabel.textContent = 'Checking availability…';
      } else if (this.__addLabelText) {
        this.addLabel.textContent = this.__addLabelText;
      }
    }
    if (this.addBtn) this.addBtn.classList.toggle('is-resolving', !!on);
  };

  /* ------------------------------------------------------------------------
     STOREFRONT RESOLUTION

     variantBySelectedOptions asks Shopify to resolve ONE variant from the
     option values. This is the whole reason for the Storefront API here:
     `product.variants` in Liquid and /products/*.js both stop at 250, and this
     bundle has 324 combinations — 74 were unreachable, all inside three duvet
     colours. Resolving by options has no such ceiling.

     @inContext carries the visitor's market, so price and compare-at come back
     as the figures Shopify will actually charge.
     ------------------------------------------------------------------------ */
  Configurator.prototype.resolveNative = function (onDone) {
    var nb = this.data.nativeBundle;
    var key = this.nativeKey();
    if (!nb || !key) { onDone && onDone(null); return; }

    this.__nvCache = this.__nvCache || {};
    var cached = this.__nvCache[key];
    if (cached && cached.state === 'done') { onDone && onDone(cached.variant); return; }
    if (cached && cached.state === 'loading') return;   /* already in flight */

    this.__nvCache[key] = { state: 'loading', variant: null };

    var d = this.groups.duvet.selected(), s = this.groups.sheet.selected();
    var sf = nb.storefront;
    var query =
      'query($h:String!,$o:[SelectedOptionInput!]!) @inContext(country: ' + (sf.country || 'CA') + ') {' +
      ' product(handle:$h){ variantBySelectedOptions(selectedOptions:$o){' +
      ' id availableForSale quantityAvailable' +
      ' price{ amount } compareAtPrice{ amount } } } }';

    var self = this;
    fetch(sf.endpoint, {
      method: 'POST',
      headers: { 'X-Shopify-Storefront-Access-Token': sf.token, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query,
        variables: {
          h: nb.handle,
          o: [
            { name: nb.optionNames.duvet, value: cellOf(self, d) },
            { name: nb.optionNames.sheet, value: cellOf(self, s) }
          ]
        }
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (j.errors) throw new Error(j.errors.map(function (e) { return e.message; }).join('; '));
        var v = j.data && j.data.product && j.data.product.variantBySelectedOptions;
        var out = v ? {
          /* cart/add.js needs the numeric id, the API returns a gid */
          id: Number(String(v.id).split('/').pop()),
          price: Math.round(Number(v.price.amount) * 100),
          compareAt: v.compareAtPrice ? Math.round(Number(v.compareAtPrice.amount) * 100) : 0,
          available: !!v.availableForSale,
          inventoryQuantity: (v.quantityAvailable === null || v.quantityAvailable === undefined) ? 0 : v.quantityAvailable
        } : null;
        self.__nvCache[key] = { state: 'done', variant: out };
        onDone && onDone(out);
      })
      .catch(function (err) {
        /* Leave no half-state: a failed lookup is recorded as resolved-to-null
           so the UI reports "unavailable" rather than showing a stale price. */
        self.__nvCache[key] = { state: 'done', variant: null };
        console.warn('[bundle-configurator] variant resolution failed: ' + err.message);
        onDone && onDone(null);
      });
  };

  /* With a Native Bundle the price is simply the resolved variant's own
     market-resolved price — no summing, because Shopify holds the figure it
     will charge. Falls back to summing the component listings only where no
     Native Bundle is wired up. */
  Configurator.prototype.basePrice = function () {
    var nv = this.nativeVariant();
    if (nv) return Number(nv.price) || 0;
    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();
    return priceOf(d) + priceOf(s);
  };

  Configurator.prototype.compareAtTotal = function () {
    var nv = this.nativeVariant();
    if (nv) return Number(nv.compareAt) || 0;
    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();
    return compareAtOf(d) + compareAtOf(s);
  };

  /* Priceable means "there is something to charge". Under a Native Bundle that
     is simply whether the combination resolved to a variant. */
  Configurator.prototype.isPriceable = function () {
    if (this.usingNativeBundle()) {
      var nv = this.nativeVariant();
      return !!(nv && Number(nv.price) > 0);
    }
    return isPriced(this.groups.duvet.selected()) &&
           isPriced(this.groups.sheet.selected());
  };

  /* What the cart will actually charge. Under a Native Bundle this is the same
     number as basePrice by construction, so the parity guard can never fire —
     which is the point of the architecture. */
  Configurator.prototype.nativeTotal = function () {
    var nv = this.nativeVariant();
    if (nv) return Number(nv.price) || 0;
    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();
    return nativePriceOf(d) + nativePriceOf(s);
  };

  /* How many bundles the two BUNDLE allocations can satisfy — the smaller of
     the two, since one of each is consumed per bundle. This is a cap on the
     quantity stepper only; it never merges the two pools, and Shopify still has
     the final say at /cart/add.js against real stock. */
  Configurator.prototype.maxQuantity = function () {
    /* Shopify already derives the bundle's availability from the limiting
       component, so under a Native Bundle its figure is authoritative and no
       min() of our own is needed. */
    var nv = this.nativeVariant();
    if (nv) {
      var q = Number(nv.inventoryQuantity) || 0;
      return q > 0 ? q : 1;
    }
    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();
    if (!d || !s) return 1;
    var max = Math.min(Number(d.bundleInventory) || 0, Number(s.bundleInventory) || 0);
    return max > 0 ? max : 1;
  };

  Configurator.prototype.quantity = function () {
    var el = this.root.querySelector('[data-bundle-quantity]');
    var n = el ? parseInt(el.value, 10) : 1;
    if (!n || n < 1) n = 1;
    var max = this.maxQuantity();
    return n > max ? max : n;
  };

  Configurator.prototype.update = function () {
    this.groups.duvet.render();
    this.groups.sheet.render();

    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();
    var root = this.root;

    var dPrice = root.querySelector('[data-bundle-duvet-price]');
    var sPrice = root.querySelector('[data-bundle-sheet-price]');
    var totalEl = root.querySelector('[data-bundle-total]');
    var subRow = root.querySelector('[data-bundle-subtotal-row]');
    var discRow = root.querySelector('[data-bundle-discount-row]');

    /* Per-component price cells were removed from the shell (the headline price
       already states the total). These calls no-op unless a theme-editor block
       reintroduces the elements. */
    renderComponentPrice(dPrice, d, this.money);
    renderComponentPrice(sPrice, s, this.money);

    /* Selection complete is not the same as priceable. Inventory decides what
       is selectable; pricing configuration decides what can be bought. Keeping
       them separate means an unpriced variant never masquerades as sold out. */
    var selected = this.groups.duvet.isComplete() && this.groups.sheet.isComplete();

    /* Under a Native Bundle the price belongs to a variant Shopify resolves for
       us. If this combination has not been fetched yet, kick it off and render
       again when it lands — the second pass finds it cached and completes
       normally. Guarded by the cache's own in-flight state, so rapid clicking
       cannot stack duplicate requests. */
    if (selected && this.usingNativeBundle() && !this.nativeVariant()) {
      var selfU = this;
      var keyBefore = this.nativeKey();
      this.resolveNative(function () {
        /* Ignore a late reply for a selection the customer has moved on from. */
        if (selfU.nativeKey() === keyBefore) selfU.update();
      });

      /* While the answer is outstanding, hold the previous price on screen and
         show the button as busy. Blanking the price or reporting "unavailable"
         here would make every swatch click flash a false error. */
      if (this.nativeState() !== 'done') {
        this.setResolving(true);
        this.addBtn.disabled = true;
        this.setError('');
        this.renderGallery();
        return;
      }
    }
    this.setResolving(false);

    var priceable = selected && this.isPriceable();
    var ready = priceable;

    var total = ready ? this.basePrice() : 0;
    var compareTotal = ready ? this.compareAtTotal() : 0;

    /* Saving is the gap between the two configured totals, so the breakdown
       rows only ever restate it. They stay hidden. */
    if (subRow) subRow.hidden = true;
    if (discRow) discRow.hidden = true;

    if (totalEl) {
      totalEl.textContent = ready ? formatMoney(total, this.money) : '—';
    }

    this.syncQuantityCap();

    if (ready) this.checkCartParity(total);

    /* Same numbers, second render target. */
    this.syncPdpPrice(total, compareTotal, ready);
    this.renderGallery();

    this.addBtn.disabled = !ready;

    /* An unpriced combination is a configuration gap, not a customer error, so
       it is stated plainly rather than left as a dead button. */
    if (selected && !priceable) {
      if (this.usingNativeBundle()) {
        /* Resolution has completed and returned nothing sellable. The component
           metafield wording below does not apply on this path — it produced the
           malformed "no bundle price is set for ." because there are no
           per-component gaps to name. */
        this.setError('That combination is currently unavailable. Please choose another.');
      } else {
        var gaps = [];
        GROUP_KEYS.forEach(function (k) {
          var item = this.groups[k].selected();
          if (!isPriced(item)) {
            gaps.push(this.groups[k].label + ' (' + item.colour + ' / ' + item.sizeLabel + ')');
          }
        }, this);
        this.setError('This combination is not available to purchase yet: no bundle price is set for ' +
          gaps.join(' and ') + '. Please choose another option.');
      }
    } else {
      this.setError('');
    }
  };

  /* ------------------------------------------------------------------------
     PDP PRICE SYNCHRONISATION
     ------------------------------------------------------------------------
     The parent product's own price ($205, compare-at $220, "6% OFF") is legacy
     and meaningless for a configured bundle. It is replaced at runtime with the
     component-derived figures.

     There is deliberately NO second calculation here: this method receives the
     already-computed subtotal and discount from update(), so the headline price
     and the configurator summary cannot drift.

     Targets are scoped strictly to [data-price-main] and
     [data-sticky-price-wrapper]. The classes inside them (.yv-product-price,
     .yv-product-compare-price) appear ~22 times per page in add-ons and
     recommendations, so querying them globally would rewrite unrelated prices.

     No shared file is modified. This is a runtime DOM update, and common.js
     cannot fight it: priceUpdate() is only ever called from .productOption
     handlers, and bundle-v2 renders zero variant pickers.
     ------------------------------------------------------------------------ */
  /* --------------------------------------------------------------------------
     PDP-vs-CART PARITY GUARD

     The cart holds the two REAL component variants, so Shopify charges
     variant.price for each. When a bundle selling price is configured that
     differs from the standalone price, the PDP total and the cart total
     diverge — the PDP is right about intent, the cart is right about money.

     Closing that gap needs a Cart Transform, which requires Shopify Plus for a
     custom app. Until one is active this reports the divergence loudly instead
     of letting it pass unnoticed. Reported once per distinct combination, so a
     customer clicking through colours does not flood the console.
     -------------------------------------------------------------------------- */
  var reportedParity = {};
  Configurator.prototype.checkCartParity = function (total) {
    var native = this.nativeTotal();
    if (native === total) return;

    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();
    var key = (d && d.variantId) + ':' + (s && s.variantId);
    if (reportedParity[key]) return;
    reportedParity[key] = true;

    console.warn(
      '[bundle-configurator] PDP total (' + (total / 100) + ') does not match what ' +
      'the cart will charge (' + (native / 100) + ') for these two variants. The cart ' +
      'holds the real component variants and Shopify charges their standalone ' +
      'prices; the configured bundle prices are display-only until a Cart ' +
      'Transform is active (requires Shopify Plus for a custom app).'
    );
  };

  /* Keeps the quantity input capped at the smaller component stock. */
  Configurator.prototype.syncQuantityCap = function () {
    var el = this.root.querySelector('[data-bundle-quantity]');
    if (!el) return;
    var max = this.maxQuantity();
    el.max = max;
    if (parseInt(el.value, 10) > max) el.value = max;
    var note = this.root.querySelector('[data-bundle-qty-note]');
    if (note) {
      var low = max <= 5;
      note.hidden = !low;
      if (low) note.textContent = 'Only ' + max + ' bundle' + (max === 1 ? '' : 's') + ' available';
    }
  };

  Configurator.prototype.syncPdpPrice = function (total, compareTotal, ready) {
    var money = this.money;

    /* Strikethrough and savings badge appear ONLY when the bundle compare-at
       total genuinely exceeds the bundle selling total. Equal or lower shows
       the selling price alone — which is also what the "no compare-at
       configured" fallback produces, since it sets compare-at equal to
       selling. */
    var showWas = ready && compareTotal > total;
    /* Floored, not rounded: the theme's own price block floors everywhere else
       on the store (tem-4 shows 6% for 6.8%), and a floored figure never claims
       a larger saving than the customer actually gets. $170 from $190 is 10.5%,
       shown as 10%. */
    var savedPct = showWas
      ? Math.floor((compareTotal - total) * 100 / compareTotal)
      : 0;

    var scope = document.querySelector('.main-product-section') || document;
    var wrappers = [];
    Array.prototype.push.apply(wrappers, scope.querySelectorAll('[data-price-main]'));
    Array.prototype.push.apply(wrappers, document.querySelectorAll('[data-sticky-price-wrapper]'));
    if (!wrappers.length) return;

    wrappers.forEach(function (box) {
      box.setAttribute('data-bundle-price-synced', 'true');

      var priceEl = box.querySelector('.yv-product-price');
      if (priceEl) priceEl.textContent = ready ? formatMoney(total, money) : '—';

      var compareBox = box.querySelector('.yv-compare-price-box');
      if (!compareBox) return;

      if (!showWas) {
        /* The legacy parent compare-at price must never masquerade as a bundle
           saving, so the box stays hidden unless the bundle really is cheaper. */
        compareBox.hidden = true;
        compareBox.style.display = 'none';
        return;
      }

      compareBox.hidden = false;
      compareBox.style.display = '';

      var compareEl = compareBox.querySelector('.yv-product-compare-price');
      if (compareEl) compareEl.textContent = formatMoney(compareTotal, money);

      var badgeEl = compareBox.querySelector('.yv-product-percent-off');
      if (badgeEl) {
        badgeEl.textContent = savedPct + '% OFF';
        badgeEl.hidden = false;
        badgeEl.style.display = '';
      }
    });
  };

  /* --------------------------------------------------------------------------
     ACCORDION REBALANCE
     --------------------------------------------------------------------------
     The four accordion blocks all render into the right column, which leaves a
     tall empty gap under the gallery on desktop. Two of them are moved beneath
     the gallery so the information sits in two balanced columns.

     The nodes are MOVED, never cloned: markup, classes, the +/- icon, hover and
     the theme's own open/close handlers all travel with the element, so the
     relocated pair behaves identically to the pair that stayed. Cloning would
     have meant a second accordion implementation to keep in step.

     Below the desktop breakpoint they return to their original position, in
     their original order, so the single-column reading order is unchanged.
     Nothing about the accordions' content is touched.
     -------------------------------------------------------------------------- */
  /* Order here is the order they appear under the gallery, which is NOT their
     order in the markup — the array is the source of truth for sequence. */
  var MOVE_BELOW_GALLERY = [
    'shipping & returns',
    'care instructions',
    'description',
    'details',
    'dimensions'
  ];
  var DESKTOP = '(min-width: 992px)';

  function accordionTitle(el) {
    var h = el.querySelector('.yv-accordion-header h6');
    return h ? h.textContent.trim().toLowerCase() : '';
  }

  Configurator.prototype.initAccordionLayout = function () {
    var gallery = document.querySelector('#yv-product-gallery-slider');
    if (!gallery) return;

    var leftCol = gallery.closest('[class*="col-"]');
    if (!leftCol) return;

    var all = Array.prototype.slice.call(
      document.querySelectorAll('.yv-product-accordion.sidebar-content'));
    var targets = all.filter(function (el) {
      return MOVE_BELOW_GALLERY.indexOf(accordionTitle(el)) !== -1;
    }).sort(function (a, b) {
      /* Present them in the configured order rather than the order the theme
         happened to render them in. */
      return MOVE_BELOW_GALLERY.indexOf(accordionTitle(a)) -
             MOVE_BELOW_GALLERY.indexOf(accordionTitle(b));
    });
    if (!targets.length) return;

    /* Remember exactly where each one came from, so the mobile restore puts it
       back in position rather than appending it to the end. */
    /* Remember the original position AND the original document order. Restoring
       has to walk that order in reverse, because an element's remembered next
       sibling may itself be one of the relocated nodes — inserting before a node
       that is not currently a child of the parent throws. */
    targets.forEach(function (el) {
      el.__homeParent = el.parentNode;
      el.__homeNext = el.nextElementSibling;
      el.__homeIndex = all.indexOf(el);
    });
    var restoreOrder = targets.slice().sort(function (a, b) {
      return b.__homeIndex - a.__homeIndex;      /* last-in-markup first */
    });

    var host = document.createElement('div');
    host.className = 'bundlecfg-gallery-accordions';
    leftCol.appendChild(host);

    var mq = window.matchMedia(DESKTOP);
    function apply() {
      if (mq.matches) {
        targets.forEach(function (el) { host.appendChild(el); });
        host.hidden = false;
      } else {
        restoreOrder.forEach(function (el) {
          if (!el.__homeParent) return;
          /* Only use the remembered anchor if it is genuinely back in place;
             otherwise append, which lands correctly because we walk from the
             last element in the original markup backwards. */
          var anchor = (el.__homeNext && el.__homeNext.parentNode === el.__homeParent)
            ? el.__homeNext : null;
          el.__homeParent.insertBefore(el, anchor);
        });
        host.hidden = true;
      }
    }
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
  };

  /* ------------------------------------------------------------------------
     MAIN GALLERY TAKEOVER
     ------------------------------------------------------------------------
     Shows the ACTIVE component's imagery in the PDP's main image area, so
     picking a duvet colour shows duvet photos and picking a sheet colour shows
     sheet photos.

     The theme's gallery is Flickity-driven with its own thumbnails and Fancybox
     zoom, and is shared by every product on the store. Rather than reaching
     into that (mutating slide counts mid-flight is how Flickity breaks), this
     hides the theme's two gallery containers and renders a simple, dependency
     free gallery in their place — and ONLY on a page where the bundle
     configurator exists. Every other product is untouched.
     ------------------------------------------------------------------------ */
  Configurator.prototype.initGallery = function () {
    var wrapper = document.querySelector('#yv-product-gallery-slider');
    if (!wrapper) return;   /* different gallery style; skip silently */

    wrapper.classList.add('bundlecfg-gallery-host');

    var gallery = document.createElement('div');
    gallery.className = 'bundlecfg-gallery';
    gallery.innerHTML =
      '<div class="bundlecfg-gallery__thumbs" data-bcfg-thumbs></div>' +
      '<div class="bundlecfg-gallery__main"><img class="bundlecfg-gallery__img" ' +
      'data-bcfg-main alt="" decoding="async"></div>';
    wrapper.appendChild(gallery);

    this.galleryEl = gallery;
    this.galleryIndex = 0;
  };

  /** @param {string} groupKey which component's imagery to show */
  Configurator.prototype.setActiveGallery = function (groupKey) {
    this.activeGroupKey = groupKey;
    this.galleryIndex = 0;
    this.renderGallery();
  };

  Configurator.prototype.renderGallery = function () {
    if (!this.galleryEl) return;

    var group = this.groups[this.activeGroupKey] || this.groups.duvet;
    var urls = group.currentImages();
    var main = this.galleryEl.querySelector('[data-bcfg-main]');
    var thumbs = this.galleryEl.querySelector('[data-bcfg-thumbs]');
    if (!main || !thumbs) return;

    /* No imagery for this colour: fall back to the theme's own gallery rather
       than showing an empty frame. */
    if (!urls.length) {
      this.galleryEl.hidden = true;
      document.querySelector('#yv-product-gallery-slider')
        .classList.remove('bundlecfg-gallery-host');
      return;
    }
    this.galleryEl.hidden = false;

    if (this.galleryIndex >= urls.length) this.galleryIndex = 0;

    var label = (group.label || '') + (group.colour ? ' in ' + group.colour : '');
    main.src = urls[this.galleryIndex];
    main.alt = label;

    var self = this;
    thumbs.textContent = '';
    urls.forEach(function (url, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'bundlecfg-gallery__thumb' +
        (i === self.galleryIndex ? ' is-active' : '');
      btn.setAttribute('aria-label', label + ' — image ' + (i + 1));
      btn.setAttribute('aria-current', i === self.galleryIndex ? 'true' : 'false');

      var img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      btn.appendChild(img);

      btn.addEventListener('click', function () {
        self.galleryIndex = i;
        self.renderGallery();
      });

      thumbs.appendChild(btn);
    });
  };

  Configurator.prototype.setError = function (message) {
    if (!this.errorEl) return;
    if (message) {
      this.errorEl.hidden = false;
      this.errorEl.textContent = message;
    } else {
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }
  };

  Configurator.prototype.setBusy = function (busy) {
    this.addBtn.disabled = busy;
    this.addBtn.classList.toggle('is-loading', busy);
  };

  /* Race-condition guard against REAL stock. Bundle availability comes from the
     bundle inventory metafields, but those are an allocation, not a promise:
     the physical units can still be gone. This re-reads live product JSON right
     before the add so a bundle allocation cannot sell something that no longer
     exists. Shopify's deny policy remains the final backstop at /cart/add.js.

     Note this checks NATIVE availability on purpose — it is the physical
     backstop underneath the allocation, not a second pricing or bundle-stock
     source. */
  Configurator.prototype.revalidate = function (items) {
    var handles = unique(items.map(function (i) { return i.handle; }));
    return Promise.all(handles.map(function (h) {
      return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'products/' + h + '.js'
        : '/products/' + h + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('Could not verify availability for ' + h);
          return r.json();
        });
    })).then(function (products) {
      var live = {};
      products.forEach(function (p) {
        (p.variants || []).forEach(function (v) { live[v.id] = v; });
      });
      var stale = [];
      items.forEach(function (i) {
        var v = live[i.variantId];
        if (!v || v.available === false) stale.push(i);
      });
      return stale;
    });
  };

  Configurator.prototype.addToCart = function () {
    var self = this;
    var d = this.groups.duvet.selected();
    var s = this.groups.sheet.selected();

    if (!d || !s) {
      this.setError('Please choose an option for both components.');
      return;
    }
    /* Under a Native Bundle, Shopify's own derived availability on the resolved
       variant is authoritative — it already accounts for the limiting
       component. Checking the two legacy component records instead would be a
       second, staler opinion. */
    var nvCheck = this.nativeVariant();
    if (nvCheck ? !nvCheck.available : (!d.available || !s.available)) {
      this.setError('That combination is currently unavailable. Please choose another.');
      return;
    }

    this.setBusy(true);
    this.setError('');

    /* ---------------------------------------------------------------------
       NATIVE BUNDLE PATH
       ---------------------------------------------------------------------
       One variant, one line. Shopify decrements both component variants,
       enforces the limiting one, and charges the very price the PDP showed —
       so there is no revalidation of two separate components to do, and no
       partner-line bookkeeping. The legacy two-line path below still runs for
       any colour not yet on the Native Bundle. */
    var nv = this.nativeVariant();
    if (nv) {
      var nqty = this.quantity();

      /* Human-readable configuration, carried on the line item so it appears in
         the cart, at checkout, and on the order for the customer and for
         fulfilment. Built from the ACTUAL resolved selections, never from the
         parent product title or a default colour.

         Visible property names (no leading underscore) on purpose: the whole
         point is that a person can read which duvet and which sheet were
         bought without decoding a variant title. */
      var props = {};
      props['Duvet'] = d.colour + ' | ' + d.sizeLabel;
      props['Sheet'] = s.colour + ' | ' + s.sizeLabel;

      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root : '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: nv.id, quantity: nqty, properties: props }] })
      }).then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) throw new Error(body.description || body.message || 'Could not add the bundle.');
          return body;
        });
      }).then(function () {
        self.setBusy(false);
        self.refreshCart();
      }).catch(function (err) {
        self.setBusy(false);
        self.setError(err.message || 'Could not add the bundle.');
      });
      return;
    }

    var pending = [
      { handle: d.handle, variantId: d.variantId, label: this.groups.duvet.label, group: this.groups.duvet },
      { handle: s.handle, variantId: s.variantId, label: this.groups.sheet.label, group: this.groups.sheet }
    ];

    this.revalidate(pending)
      .then(function (stale) {
        if (stale.length) {
          /* Mark the offending items unavailable locally so the UI corrects
             itself immediately, then report exactly which one failed. */
          stale.forEach(function (entry) {
            entry.group.items.forEach(function (it) {
              if (it.variantId === entry.variantId) it.available = false;
            });
          });
          self.update();
          var names = stale.map(function (e) { return e.label; }).join(' and ');
          throw new Error(names + ' just sold out in the option you picked. Please choose another.');
        }

        var dSel = self.groups.duvet.selected();
        var sSel = self.groups.sheet.selected();
        var bundleId = bundleConfigKey(dSel.variantId, sSel.variantId);
        /* Fresh instance id for every add, so repeat adds of the same
           configuration remain separately addressable bundles. */
        self.__instanceId = bundleInstanceId();

        /* The SAME quantity goes to both components, so a bundle can never be
           added half-complete. Shopify then validates that quantity against
           each component's real stock and rejects the whole add if either
           falls short. */
        var qty = self.quantity();

        var payload = {
          items: [
            {
              id: dSel.variantId,
              quantity: qty,
              properties: buildProperties(self, bundleId, 'duvet', dSel, sSel)
            },
            {
              id: sSel.variantId,
              quantity: qty,
              properties: buildProperties(self, bundleId, 'sheet', sSel, dSel)
            }
          ]
        };

        return fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root
          ? window.Shopify.routes.root : '/') + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        }).then(function (res) {
          return res.json().then(function (body) {
            if (!res.ok) {
              throw new Error(body.description || body.message || 'Could not add this bundle to your cart.');
            }
            return body;
          });
        });
      })
      .then(function () {
        self.setBusy(false);
        self.refreshCart();
      })
      .catch(function (err) {
        self.setBusy(false);
        self.setError(err.message || 'Something went wrong. Please try again.');
      });
  };

  /* DETERMINISTIC BUNDLE IDENTITY
     ---------------------------------------------------------------------
     `_bundle_config` is derived purely from the two selected component
     variants, so the same configuration always produces byte-identical
     properties on both lines.

     That matters for two reasons:

       1. Shopify merges cart lines only when the variant AND every property
          match. The previous random per-add id made merging impossible, so
          adding the same bundle twice produced duplicate lines instead of
          quantity 2. A deterministic key fixes that natively, with no
          client-side matching.

       2. It gives the Cart Transform a stable grouping key for merging the
          two component lines into one customer-facing bundle line.

     Different combinations naturally yield a different key and stay separate.
  */
  function bundleConfigKey(duvetVariantId, sheetVariantId) {
    return 'd' + duvetVariantId + '-s' + sheetVariantId;
  }

  /* BUNDLE INSTANCE IDENTITY
     ---------------------------------------------------------------------
     `_bundle_config` identifies the CONFIGURATION (which duvet + which
     sheet). `_bundle_id` identifies THIS PARTICULAR bundle, and is unique
     per add.

     Two consequences, both intended:
       - Two adds of the same configuration stay as two independent bundle
         instances rather than merging into quantity 2, because their
         properties differ.
       - The cart-integrity script can pair exactly the two lines belonging
         to one instance, so acting on one bundle never disturbs another
         that happens to have identical contents. */
  function bundleInstanceId() {
    return 'bi-' + Date.now().toString(36) + '-' +
      Math.random().toString(36).slice(2, 8);
  }

  function buildProperties(cfg, bundleId, role, self, other) {
    var props = {};
    props._bundle_id = cfg.__instanceId;      /* THIS bundle instance */
    props._bundle_config = bundleId;          /* which configuration it is */
    props._bundle_parent_id = String(cfg.bundle.parentVariantGid || cfg.bundle.parentProductId);
    props._bundle_parent_handle = cfg.bundle.parentHandle;
    props._bundle_role = role;
    props._bundle_component_variant_id = String(self.variantId);
    props._bundle_partner_variant_id = String(other.variantId);
    props._bundle_version = 'v1';
    /* visible to the customer in cart / checkout / order */
    props['Bundle'] = cfg.bundle.parentTitle;
    props[cfg.groups[role].label] = self.size + ' / ' + self.colour;
    return props;
  }

  /* Reuse the theme's existing cart refresh when it is present. No theme file
     is modified; these globals are declared by snippets/theme-scripts.liquid
     and assets/cart-ajax.js which we leave untouched. */
  Configurator.prototype.refreshCart = function () {
    var handled = false;

    if (typeof window.jQuery !== 'undefined' &&
        typeof window.buildCart === 'function' &&
        typeof cartUrl !== 'undefined') {
      try {
        window.jQuery.getJSON(cartUrl, function (cart) {
          window.jQuery('body').addClass('side_Drawer_open');
          window.buildCart(cart, true);
        });
        handled = true;
      } catch (e) { handled = false; }
    }

    document.dispatchEvent(new CustomEvent('bundle:added'));

    if (!handled) {
      window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root : '/') + 'cart';
    }
  };

  /* ------------------------------------------------------------- bootstrap */

  function init(scope) {
    (scope || document).querySelectorAll('[data-bundle-configurator]').forEach(function (root) {
      if (!root.__bundle) new Configurator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* Theme editor support */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
